import { DataStoreType } from "../../../stores/dataStore";
import useMainStore from "../../../stores/mainStore";
import { toast } from "react-toastify";
import { supabase } from "../../../context/useSupabaseContext";
import formatDateForDatabase from "../../formatDateForDatabase";

export default async function upsertTableData(tableData: DataStoreType["data"]): Promise<boolean> {
    if (!tableData) return false;

    if (
        isEmpty(tableData.folders) &&
        isEmpty(tableData.notes) &&
        isEmpty(tableData.tags) &&
        isEmpty(tableData.deleted?.folders) &&
        isEmpty(tableData.deleted?.notes) &&
        isEmpty(tableData.deleted?.tags)
    ) {
        return false;
    }

    if (typeof tableData !== 'object') {
        return false;
    }

    if (!tableData.hasOwnProperty('notes') || typeof tableData.notes !== 'object') {
        return false;
    }

    if (!tableData.hasOwnProperty('folders') || typeof tableData.folders !== 'object') {
        return false;
    }

    if (!tableData.hasOwnProperty('tags') || typeof tableData.tags !== 'object') {
        return false;
    }

    console.log("UPSERT TABLE");

    useMainStore.getState().syncFunctions.addIsUploading("tableUpload");

    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            console.error("No authenticated user found");
            useMainStore.getState().syncFunctions.removeIsUploading("tableUpload");
            return false;
        }

        const userId = user.id;

        const formattedFolders = Object.values(tableData.folders)
            .filter((folder) => folder.id)
            .map(
                (folder) => ({
                    user_id: userId,
                    id: folder.id,
                    name: folder.name,
                    color: folder.color,
                    created_at: formatDateForDatabase(folder.createdAt) || new Date().toISOString(),
                    updated_at: formatDateForDatabase(folder.updatedAt) || new Date().toISOString(),
                })
            );

        const formattedNotes = Object.values(tableData.notes)
            .filter((note) => note.id)
            .map(
                (note) => ({
                    user_id: userId,
                    id: note.id,
                    title: note.title,
                    created_at: formatDateForDatabase(note.createdAt) || new Date().toISOString(),
                    updated_at: formatDateForDatabase(note.updatedAt) || new Date().toISOString(),
                    folder_id: note.folderID || undefined,
                    tag_ids: note.tagIDs || [],
                })
            );

        const deletedData = {
            user_id: userId,
            notes: tableData.deleted?.notes || undefined,
            folders: tableData.deleted?.folders || undefined,
            tags: tableData.deleted?.tags || undefined,
        };

        const hasDeletedData =
            (tableData.deleted?.notes && tableData.deleted.notes.length > 0) ||
            (tableData.deleted?.folders && tableData.deleted.folders.length > 0) ||
            (tableData.deleted?.tags && tableData.deleted.tags.length > 0);

        if (
            formattedFolders.length === 0 &&
            formattedNotes.length === 0 &&
            !hasDeletedData
        ) {
            useMainStore.getState().syncFunctions.removeIsUploading("tableUpload");
            return true;
        }

        console.log("Upserting folders:", formattedFolders);
        console.log("Upserting notes:", formattedNotes);
        console.log("Upserting deleted data:", deletedData);

        const [folderUpsert, noteUpsert, deleteUpsert] = await Promise.all([
            supabase.from("folders").upsert(formattedFolders),
            supabase.from("notes").upsert(formattedNotes),
            supabase.from("deleted").upsert(deletedData),
        ]);

        if (folderUpsert.error) {
            console.error("Folder upsert error:", folderUpsert.error);
            throw new Error(`Folder upsert failed: ${folderUpsert.error.message}`);
        }
        if (noteUpsert.error) {
            console.error("Note upsert error:", noteUpsert.error);
            throw new Error(`Note upsert failed: ${noteUpsert.error.message}`);
        }
        if (deleteUpsert.error) {
            console.error("Delete upsert error:", deleteUpsert.error);
            throw new Error(`Delete upsert failed: ${deleteUpsert.error.message}`);
        }

        useMainStore.getState().syncFunctions.removeIsUploading("tableUpload");
        useMainStore.getState().syncFunctions.removeChangesMade("tableChanged");

        return true;
    } catch (error) {
        console.error("Error upserting table data:", error);
        toast.error("Failed to upload table data");
        useMainStore.getState().syncFunctions.removeIsUploading("tableUpload");
        return false;
    }
}

const isEmpty = (obj: Record<string, any> | undefined) =>
    !obj || Object.keys(obj).length === 0;
